/**
 * CSV helpers shared by every form that offers a bulk Import / Template.
 *
 * Extracted from `features/modules/ModuleListView.tsx`, which held the only copy
 * as a private function: a second form needing it (System › Message Library)
 * would otherwise have had to duplicate it or import the whole 3,000-line list
 * component for one parser. ModuleListView now imports it from here, so there is
 * exactly one implementation (standing rule 8).
 */

/** Splits one CSV line, honouring quoted fields and `""` escapes. */
function parseLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else quoted = false;
      } else cur += c;
    } else if (c === ',') {
      out.push(cur);
      cur = '';
    } else if (c === '"') {
      quoted = true;
    } else cur += c;
  }
  out.push(cur);
  return out;
}

/**
 * CSV text → one object per data row, keyed by the header-row labels.
 *
 * Returns an empty array for an empty file or a header with no rows, so a caller
 * can treat "nothing to import" as data rather than an error.
 */
export function parseCsv(text: string): Record<string, string>[] {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');
  if (lines.length < 2) return [];

  const headers = parseLine(lines[0]!).map((h) => h.trim());
  return lines.slice(1).map((l) => {
    const cells = parseLine(l);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = (cells[i] ?? '').trim();
    });
    return obj;
  });
}

/** Quotes one value for CSV output when it contains a comma, quote or newline. */
export function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Rows + headers → CSV text, CRLF-terminated as Excel expects. */
export function toCsv(headers: readonly string[], rows: readonly (readonly unknown[])[]): string {
  return [headers, ...rows]
    .map((r) => r.map(csvCell).join(','))
    .join('\r\n');
}

/**
 * Offers `text` to the browser as a file download.
 *
 * The leading BOM is what makes Excel read the file as UTF-8; without it a
 * message containing an accented character or a degree sign opens as mojibake.
 */
export function downloadCsv(filename: string, text: string): void {
  const url = URL.createObjectURL(
    new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
